import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

/**
 * The app's colour-scheme preference, and the one place that writes `data-theme`.
 *
 * `tokens.css` has carried a full dark ladder since § 54, behind
 * `:root[data-theme="dark"]`, and `Layout.tsx` loads it on every authed screen — so
 * the plumbing has been complete for a while. Until now the only thing that set the
 * attribute was the showcase's preview toggle, which cleared it on unmount. This is
 * the persistent version.
 *
 * **Three preferences, two themes.** `system` is not a third look — it resolves to
 * `light` or `dark` from the OS and then keeps following it, which is why the media
 * query is subscribed to rather than read once.
 *
 * **`light` removes the attribute rather than setting `data-theme="light"`.**
 * `tokens.css` defines light on bare `:root` and only overrides under
 * `[data-theme="dark"]`; there is no `[data-theme="light"]` block for an explicit
 * value to match. Writing one would be a decorative attribute that no CSS reads, and
 * it would quietly break the moment someone wrote `:root:not([data-theme])` expecting
 * it to mean "default".
 *
 * ⚠ **Coverage, measured 2026-08-30.** `DesignSystem` is 96% semantic tokens and
 * `Templates` 75%, but `Pages/GMS` is **5%** — 673 raw hex literals and 3,009
 * legacy-palette classes that no `data-theme` can reach. Dark is therefore gated to
 * `compatioAdmin` until the module-by-module migration closes that gap.
 *
 * **`allowDark` exists because a gate has two halves, and hiding a control is only
 * one of them** (§ 187). While dark was admin-only, gating just the profile-menu
 * control left every non-admin whose OS asked for dark still getting
 * `data-theme="dark"` — the default preference is `system`, and `system` asks the OS.
 * So callers pass `allowDark`, and when it is false everything resolves to light no
 * matter what is stored or what the OS says.
 *
 * **Nothing passes it any more** — the role gate was removed on 2026-09-14 and theme
 * switching is open to every signed-in user. The parameter stays, defaulting true,
 * because re-gating should cost one argument rather than a re-plumbing. Either way
 * this module stays out of the WHO: `allowDark` is a boolean the caller derives, never
 * a role check in here.
 */

export type ThemePreference = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'gms-theme-preference';
export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'system';

/**
 * The app-wide theme lock (2026-09-25). While set, every user gets this theme: the
 * picker shows it selected with the other options disabled, `setPreference` is a
 * no-op, and a stored `dark` / `system` choice is IGNORED — not erased. The stored
 * value is left in localStorage on purpose, so setting this back to `null` restores
 * each user's own choice with no migration. `null` = unlocked.
 */
export const THEME_LOCKED_TO: ResolvedTheme | null = 'light';

const DARK_QUERY = '(prefers-color-scheme: dark)';

const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'system' || value === 'light' || value === 'dark';

/**
 * Reads the stored preference, falling back to `system`.
 *
 * Wrapped in try/catch because `localStorage` throws rather than returning null in
 * Safari's private mode and under some enterprise storage policies — an app that
 * cannot read a colour preference should still boot.
 */
export const readThemePreference = (): ThemePreference => {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
};

export const writeThemePreference = (preference: ThemePreference): void => {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* Same reasoning as the read: a storage failure must not break the toggle. The
       preference still applies for this session; it just will not survive a reload. */
  }
};

/** Does the OS currently ask for dark? `false` wherever `matchMedia` is missing (jsdom). */
export const systemPrefersDark = (): boolean => {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_QUERY).matches;
};

export const resolveTheme = (
  preference: ThemePreference,
  prefersDark: boolean,
  /* Default true so the existing call shape keeps meaning what it meant. False is
     the § 187 gate: light, unconditionally — a stored `dark` from an admin session
     must not leak through on an account that has since lost the role. */
  allowDark: boolean = true,
): ResolvedTheme => {
  if (!allowDark) return 'light';
  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return preference;
};

/** The single writer of `data-theme`. See the note above on why light REMOVES it. */
export const applyResolvedTheme = (resolved: ResolvedTheme): void => {
  if (resolved === 'dark') document.documentElement.dataset.theme = 'dark';
  else delete document.documentElement.dataset.theme;
};

/**
 * Owns the preference for the app shell. Mount it once — `Layout` does — and read
 * `preference` / call `setPreference` from wherever the control lives.
 *
 * The media-query subscription is unconditional rather than only while `preference
 * === 'system'`. Subscribing conditionally would mean tearing the listener down and
 * back up on every change, and `resolveTheme` already ignores `prefersDark` unless
 * the preference is `system` — so the extra subscription costs one boolean of state
 * and removes a lifecycle branch.
 */
export const useThemePreference = (options?: { allowDark?: boolean; lockedTo?: ResolvedTheme | null }) => {
  /* `lockedTo` defaults to the app-wide lock; tests of the switching logic pass `null`.
     Read with `'lockedTo' in options` so an explicit `lockedTo: null` means "unlocked",
     not "use the default". */
  const { allowDark = true } = options ?? {};
  const lockedTo = options && 'lockedTo' in options ? options.lockedTo ?? null : THEME_LOCKED_TO;
  const [preference, setPreferenceState] = useState<ThemePreference>(readThemePreference);
  const [prefersDark, setPrefersDark] = useState<boolean>(systemPrefersDark);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setPrefersDark(event.matches);
    /* `addEventListener` where available; Safari < 14 only has the deprecated
       `addListener`, and this app still supports it elsewhere. */
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    query.addListener(onChange);
    return () => query.removeListener(onChange);
  }, []);

  const resolved: ResolvedTheme = lockedTo ?? resolveTheme(preference, prefersDark, allowDark);

  /*
    § 408 — `useLayoutEffect`, so the attribute is on <html> before the browser paints the
    shell. This was `useEffect`, on the reasoning that "the attribute lands before paint either
    way because the browser has not yet had a frame". It does not: a passive effect from a
    non-urgent render (the shell mounting) runs AFTER the frame it rendered is painted, so a
    dark user saw one frame of the light shell, then the switch. A layout effect runs after the
    DOM is written and before that paint. The app renders on the client only, so the SSR
    warning that once argued for `useEffect` does not arise. (While `THEME_LOCKED_TO` is set,
    every frame is light and nothing shows; this is for the day it is lifted.)
  */
  useLayoutEffect(() => {
    applyResolvedTheme(resolved);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    // Locked: the picker's other options are disabled, and this refuses anything that
    // still reaches it — nothing is written, so the user's stored choice survives.
    if (lockedTo) return;
    setPreferenceState(next);
    writeThemePreference(next);
  }, [lockedTo]);

  return { preference: lockedTo ?? preference, setPreference, resolved, locked: lockedTo !== null };
};
